import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ScheduledPostTarget } from 'types'
import { ValidationResult, validateScheduledPost } from 'utils/validateScheduledPost'

export type IntegrationStatus = 'failed' | 'reauth' | 'warning' | 'success' | 'none'

export interface IntegrationStatusResult {
  status: IntegrationStatus
  icon: 'check' | 'alert'
  overlayClass: string
  iconColor: string
}

/**
 * Core logic to compute status result from target, validation, and auth state
 * Priority: failed > reauth > warning > success
 * Shared by both IconWithStatus and IntegrationSelector
 */
export function computeStatusResult(
  target: ScheduledPostTarget | undefined,
  needsReauth: boolean,
  validation: ValidationResult,
): IntegrationStatusResult {
  const targetStatus = target?.status

  // Priority 1: Failed
  if (targetStatus === 'failed') {
    return {
      status: 'failed',
      icon: 'alert',
      overlayClass: 'errorOverlay',
      iconColor: 'var(--light-100)',
    }
  }

  // Priority 4: Published/success (check before reauth/warning to match original logic)
  if (targetStatus === 'published') {
    return {
      status: 'success',
      icon: 'check',
      overlayClass: 'successOverlay',
      iconColor: 'var(--light-100)',
    }
  }

  // Priority 2: Needs reauth (only if not failed or published)
  if (needsReauth) {
    return {
      status: 'reauth',
      icon: 'alert',
      overlayClass: 'reauthOverlay',
      iconColor: 'var(--light-100)',
    }
  }

  // Priority 3: Validation warning (only if not failed, published, or needsReauth)
  if (!validation.isValid) {
    return {
      status: 'warning',
      icon: 'alert',
      overlayClass: 'warningOverlay',
      iconColor: 'var(--dark-80)',
    }
  }

  // No status
  return {
    status: 'none',
    icon: 'alert',
    overlayClass: '',
    iconColor: '',
  }
}

/**
 * Determines the status of an integration following IconWithStatus priority logic
 * Priority: failed > reauth > warning > success
 */
export function getIntegrationStatus(
  integration: ExternalIntegration,
  target: ScheduledPostTarget | undefined,
  needsReauth: boolean,
  validation: ValidationResult,
): IntegrationStatusResult {
  return computeStatusResult(target, needsReauth, validation)
}

/**
 * Computes aggregate status for multiple integrations
 * Uses priority order: failed > reauth > warning > success
 * Returns highest priority status found
 */
export function getAggregateStatus(
  selectedIntegrations: ExternalIntegration[],
  targets: ScheduledPostTarget[],
  accountIdsNeedReauth: number[],
  scheduledPost: ScheduledPostShow | null,
  platform: ExternalIntegrationEnum,
): IntegrationStatusResult {
  const statuses = selectedIntegrations.map((integration) => {
    const target = targets.find((t) => t.provider === platform && t.external_integration_id === integration.id)
    const needsReauth = accountIdsNeedReauth.includes(integration.id)
    const validation = validateScheduledPost(scheduledPost, platform, target)

    return getIntegrationStatus(integration, target, needsReauth, validation)
  })

  // Apply priority logic: failed > reauth > warning > success
  const failedStatus = statuses.find((s) => s.status === 'failed')
  if (failedStatus) {
    return failedStatus
  }

  const reauthStatus = statuses.find((s) => s.status === 'reauth')
  if (reauthStatus) {
    return reauthStatus
  }

  const warningStatus = statuses.find((s) => s.status === 'warning')
  if (warningStatus) {
    return warningStatus
  }

  if (statuses.length > 0 && statuses.every((s) => s.status === 'success')) {
    return statuses[0] // All success, return success status
  }

  // No status
  return {
    status: 'none',
    icon: 'alert',
    overlayClass: '',
    iconColor: '',
  }
}
